/**
 * /what-matters — USP-selector screen shown before /login for new users
 * assigned to the `what_matters` A/B-bucket.
 *
 * Flow:
 *   - Hero + prompt ("Hvad skal din agent starte med?")
 *   - 5 toggleable USP cards (full-card tap target, subtle selection state)
 *   - Primary CTA "Fortsæt" (disabled when 0 selected)
 *   - Skip link "Spring over" (saves empty array so login.tsx doesn't re-redirect)
 *   - Top-right "Log ind"-link for returning users
 *
 * Selection persists to storage via USP_SELECTION_KEY; login.tsx reads it
 * on mount and includes it in the pre-register body. Backend lead row
 * inherits usp_selection; MitID callback copies it to user.usp_selection.
 */
import { View, Text, Pressable, StyleSheet, ScrollView, Platform, useWindowDimensions } from 'react-native'
import { useState, useEffect } from 'react'
import { useRouter } from 'expo-router'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { Check, Wallet, ListChecks, Bell, PhoneOff, ShieldAlert, ChevronRight } from 'lucide-react-native'
import { colors, spacing, fontFamily, shadows } from '../../src/theme'
import { events, registerAbBucket } from '../../src/lib/posthog'
import { storage, USP_SELECTION_KEY, HAS_LOGGED_IN_KEY, AB_BUCKET_KEY } from '../../src/utils/storage'
import { haptics } from '../../src/utils/haptics'

type UspCode = 'save_money' | 'overview' | 'price_alerts' | 'skip_calls' | 'illegal_fees'

const USPS: { code: UspCode; title: string; icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }> }[] = [
  { code: 'save_money', title: 'Spare på regningerne', icon: Wallet },
  { code: 'overview', title: 'Samle alle aftaler ét sted', icon: ListChecks },
  { code: 'price_alerts', title: 'Få varsel når priser stiger', icon: Bell },
  { code: 'skip_calls', title: 'Slippe for opkald til udbydere', icon: PhoneOff },
  { code: 'illegal_fees', title: 'Fange gebyrer du ikke burde betale', icon: ShieldAlert },
]

export default function WhatMattersScreen() {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const { height: screenHeight } = useWindowDimensions()
  const [selected, setSelected] = useState<UspCode[]>([])
  const [isReturning, setIsReturning] = useState(false)

  // Responsive sizing — same lerp pattern as login.tsx
  const s = Math.min(1, Math.max(0, (screenHeight - 667) / (932 - 667)))
  const lerp = (min: number, max: number) => Math.round(min + (max - min) * s)

  useEffect(() => {
    events.whatMattersViewed()
    storage.get(HAS_LOGGED_IN_KEY).then(val => {
      if (val === '1') setIsReturning(true)
    })
    // Edge case: deep-link to /what-matters without going through login guard.
    // Ensure ab_bucket is registered as super-property so downstream events are tagged.
    storage.get(AB_BUCKET_KEY).then(async (cached) => {
      if (cached !== 'what_matters' && cached !== 'direct') {
        await storage.set(AB_BUCKET_KEY, 'what_matters')
        events.abBucketAssigned('what_matters_v1', 'what_matters')
      }
      registerAbBucket('what_matters_v1', cached || 'what_matters')
    })
  }, [])

  const toggle = (code: UspCode) => {
    haptics.selection()
    setSelected(prev => {
      const isSelected = prev.includes(code)
      const next = isSelected ? prev.filter(c => c !== code) : [...prev, code]
      events.uspToggled(code, !isSelected, next.length)
      return next
    })
  }

  const handleContinue = async () => {
    if (selected.length === 0) return
    await storage.set(USP_SELECTION_KEY, JSON.stringify(selected))
    events.whatMattersContinued(selected)
    router.replace('/login')
  }

  const handleSkip = async () => {
    await storage.set(USP_SELECTION_KEY, JSON.stringify([]))
    events.whatMattersSkipped()
    router.replace('/login')
  }

  const handleLoginInstead = async () => {
    await storage.set(USP_SELECTION_KEY, JSON.stringify([]))
    router.replace('/login')
  }

  return (
    <SafeAreaView style={styles.container}>
      {isReturning && (
        <Pressable style={styles.loginInstead} onPress={handleLoginInstead} hitSlop={12}>
          <Text style={styles.loginInsteadText}>Log ind</Text>
        </Pressable>
      )}

      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: 140 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Hero — no logo (user is already in the app). Left-aligned for modern feel */}
        <View style={[styles.heroSection, { marginTop: lerp(20, 36), marginBottom: lerp(24, 36) }]}>
          <Text style={[styles.heroTitle, { fontSize: lerp(28, 34) }]}>Hvad vil du have styr på?</Text>
          <Text style={styles.heroSubtext}>Jeg tilpasser mig dig.</Text>
        </View>

        {/* USP cards — full-card tap target, chevron→check on selection */}
        <View style={styles.cardsContainer}>
          {USPS.map(({ code, title, icon: Icon }) => {
            const isSelected = selected.includes(code)
            return (
              <Pressable
                key={code}
                onPress={() => toggle(code)}
                style={({ pressed }) => [
                  styles.uspCard,
                  isSelected && styles.uspCardSelected,
                  pressed && !isSelected && styles.uspCardPressed,
                ]}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isSelected }}
                accessibilityLabel={title}
              >
                <Icon
                  size={26}
                  strokeWidth={2}
                  color={isSelected ? colors.primary : colors.text.secondary}
                />
                <Text style={[styles.uspTitle, isSelected && styles.uspTitleSelected]} numberOfLines={2}>
                  {title}
                </Text>
                {isSelected ? (
                  <View style={styles.uspCheck}>
                    <Check size={16} color="#FFFFFF" strokeWidth={3} />
                  </View>
                ) : (
                  <ChevronRight size={20} color={colors.text.muted} strokeWidth={2} />
                )}
              </Pressable>
            )
          })}
        </View>
      </ScrollView>

      {/* Sticky bottom CTA — always reachable on mobile */}
      <View style={[styles.stickyBottom, { paddingBottom: Math.max(insets.bottom, 12) }]}>
        <Pressable
          onPress={handleContinue}
          disabled={selected.length === 0}
          style={({ pressed }) => [
            styles.continueButton,
            selected.length === 0 && styles.continueButtonDisabled,
            pressed && selected.length > 0 && styles.continueButtonPressed,
          ]}
        >
          <Text style={styles.continueButtonText}>
            Fortsæt{selected.length > 0 ? ` (${selected.length})` : ''}
          </Text>
        </Pressable>

        <Pressable onPress={handleSkip} style={styles.skipButton} hitSlop={12}>
          <Text style={styles.skipText}>Spring over</Text>
        </Pressable>
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: spacing.md,
    paddingTop: Platform.OS === 'web' ? spacing.md : spacing.sm,
    paddingBottom: spacing.xl,
    maxWidth: 480,
    alignSelf: 'center',
    width: '100%',
  },
  loginInstead: {
    position: 'absolute',
    top: Platform.OS === 'web' ? spacing.md : spacing.lg,
    right: spacing.lg,
    zIndex: 1,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  loginInsteadText: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 14,
    color: colors.primary,
  },
  heroSection: {
    // Left-aligned for modern mobile feel (Revolut/Monzo pattern)
  },
  heroTitle: {
    fontWeight: '700',
    fontFamily: fontFamily.bold,
    color: colors.text.primary,
    lineHeight: 40,
    letterSpacing: -0.5,
  },
  heroSubtext: {
    fontFamily: fontFamily.medium,
    fontWeight: '500',
    fontSize: 16,
    color: colors.text.secondary,
    marginTop: 10,
    lineHeight: 22,
  },
  cardsContainer: {
    gap: 12,
  },
  uspCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 16,
    paddingVertical: 18,
    paddingHorizontal: 18,
    minHeight: 72,
    ...shadows.sm,
  },
  uspCardSelected: {
    borderColor: colors.primary,
    borderWidth: 1.5,
    backgroundColor: colors.primarySubtle,
  },
  uspCardPressed: {
    backgroundColor: colors.surfaceHover,
    borderColor: colors.text.muted,
  },
  uspTitle: {
    flex: 1,
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 17,
    color: colors.text.primary,
    letterSpacing: -0.15,
    lineHeight: 22,
  },
  uspTitleSelected: {
    color: colors.primaryDark,
  },
  uspCheck: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stickyBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.background,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  continueButton: {
    backgroundColor: colors.primary,
    paddingVertical: 15,
    borderRadius: 14,
    alignItems: 'center',
    minHeight: 52,
    justifyContent: 'center',
    ...shadows.colored(colors.primary),
  },
  continueButtonDisabled: {
    opacity: 0.35,
  },
  continueButtonPressed: {
    backgroundColor: colors.primaryDark,
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontFamily: fontFamily.bold,
    fontWeight: '700',
    fontSize: 16,
  },
  skipButton: {
    alignItems: 'center',
    paddingVertical: 14,
    marginTop: 2,
  },
  skipText: {
    fontFamily: fontFamily.semiBold,
    fontWeight: '600',
    fontSize: 14,
    color: colors.text.muted,
  },
})
